import { useState, useEffect, useRef, useCallback } from 'react';
import api, { tokenAPI } from '../api';
import toast from 'react-hot-toast';
import {
  HiPlus, HiPencil, HiTrash, HiX, HiUpload, HiPlay, HiEye, HiEyeOff,
  HiKey, HiRefresh,
} from 'react-icons/hi';

const emptyForm = {
  name: '',
  description: '',
  streamUrl: '',
  thumbnail: '',
  category: 'General',
  linkType: 'HLS',
  drmKid: '',
  drmKey: '',
  requiresPayment: false,
  visibleOnWebsite: true,
  hidden: false,
  isActive: true,
  sortOrder: 0,
};

const CATEGORY_PRESETS = ['Sport', 'Movie', 'Music', 'News', 'Kids', 'Religion', 'General', 'Wild', 'FREE CHANNELS', 'Sports', 'Entertainment', 'Movies', 'Documentary'];
const LINK_TYPES = ['HLS', 'DASH', 'DRM'];

function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export default function VideoChannels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // --- CDN token state ---
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [manualToken, setManualToken] = useState('');
  const [tokenSaving, setTokenSaving] = useState(false);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await api.get('/admin/videos');
      if (res.data.success) setChannels(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchToken = useCallback(async () => {
    setTokenLoading(true);
    try {
      const res = await tokenAPI.get();
      if (res.data.success) setTokenInfo(res.data.data);
    } catch (err) {
      console.error(err);
      setTokenInfo({ token: '', override: '', envConfigured: false, updatedAt: null });
    } finally {
      setTokenLoading(false);
    }
  }, []);

  useEffect(() => { fetchChannels(); fetchToken(); }, [fetchChannels, fetchToken]);

  const handleSaveToken = async () => {
    if (manualToken.trim() === '') {
      toast.error('Paste a token first');
      return;
    }
    setTokenSaving(true);
    try {
      const res = await tokenAPI.set(manualToken.trim());
      if (res.data.success) {
        toast.success('CDN token saved');
        setManualToken('');
        fetchToken();
      } else {
        toast.error(res.data.message || 'Failed to save token');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save token');
    } finally {
      setTokenSaving(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (channel) => {
    setEditing(channel);
    setForm({
      name: channel.name,
      description: channel.description || '',
      streamUrl: channel.streamUrl,
      thumbnail: channel.thumbnail || '',
      category: channel.category || 'General',
      linkType: channel.linkType || 'HLS',
      drmKid: channel.drmKid || '',
      drmKey: channel.drmKey || '',
      requiresPayment: !!channel.requiresPayment,
      visibleOnWebsite: channel.visibleOnWebsite !== false,
      hidden: !!channel.hidden,
      isActive: channel.isActive !== false,
      sortOrder: channel.sortOrder || 0,
    });
    setShowModal(true);
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        const res = await api.post('/admin/videos/upload-thumbnail', { image: base64 });
        if (res.data.success) {
          setForm((f) => ({ ...f, thumbnail: res.data.data.url }));
          toast.success('Thumbnail uploaded');
        } else {
          toast.error(res.data.message || 'Upload failed');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.streamUrl.trim()) {
      toast.error('Name and Stream URL are required');
      return;
    }
    if (form.linkType === 'DRM' && !form.drmKid.trim()) {
      toast.error('DRM channels require a DRM Key ID (kid)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder) || 0,
      };

      let res;
      if (editing) {
        res = await api.put(`/admin/videos/${editing._id}`, payload);
      } else {
        res = await api.post('/admin/videos', payload);
      }

      if (res.data.success) {
        toast.success(editing ? 'Channel updated' : 'Channel created');
        setShowModal(false);
        fetchChannels();
      } else {
        toast.error(res.data.message || 'Failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video channel?')) return;
    try {
      const res = await api.delete(`/admin/videos/${id}`);
      if (res.data.success) {
        toast.success('Channel deleted');
        fetchChannels();
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (channel) => {
    try {
      const res = await api.put(`/admin/videos/${channel._id}`, {
        isActive: !channel.isActive,
      });
      if (res.data.success) {
        toast.success(channel.isActive ? 'Channel hidden' : 'Channel visible');
        fetchChannels();
      }
    } catch (err) {
      toast.error('Update failed');
    }
  };

  if (loading) return <div className="page-loading">Loading video channels...</div>;

  // Compute token status for display
  let tokenExpired = false;
  let tokenExpiresAt = null;
  if (tokenInfo && tokenInfo.token) {
    const payload = decodeJwtPayload(tokenInfo.token);
    if (payload && payload.exp) {
      tokenExpiresAt = new Date(payload.exp * 1000);
      tokenExpired = Date.now() > tokenExpiresAt.getTime();
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Video Channels</h1>
          <p className="page-subtitle">{channels.length} channel{channels.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <HiPlus size={18} /> Add Channel
        </button>
      </div>

      {/* CDN Token Dashboard */}
      <div style={{
        background: '#FFFFFF', borderRadius: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: 20, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <HiKey size={18} color="#06B6D4" /> CDN Token
            {!tokenLoading && (
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                  background: tokenExpired ? '#FEF2F2' : '#ECFDF5',
                  color: tokenExpired ? '#EF4444' : '#10B981',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: tokenExpired ? '#EF4444' : '#10B981' }} />
                {tokenExpired ? 'EXPIRED' : 'ACTIVE'}
              </span>
            )}
          </h2>
          <button className="btn-secondary" onClick={fetchToken} style={{ padding: '6px 12px', fontSize: 12 }}>
            <HiRefresh size={14} /> Refresh
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div style={statBoxStyle}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Source</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {tokenInfo?.override ? 'Admin override' : tokenInfo?.envConfigured ? 'Server env (CDN_TOKEN)' : 'Not configured'}
            </div>
          </div>
          <div style={statBoxStyle}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Expires</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {tokenExpiresAt ? tokenExpiresAt.toLocaleString() : 'Unknown / no expiry'}
            </div>
          </div>
          <div style={statBoxStyle}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Last Updated</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {tokenInfo?.updatedAt ? new Date(tokenInfo.updatedAt).toLocaleString() : '—'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            placeholder="Paste a fresh cdntoken here (overrides server env)"
            style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}
          />
          <button className="btn-primary" onClick={handleSaveToken} disabled={tokenSaving} style={{ whiteSpace: 'nowrap' }}>
            {tokenSaving ? 'Saving...' : 'Save Token'}
          </button>
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 8 }}>
          The player auto-refreshes this token every 60s. DRM channels (Azam, zapi, edgenext) need a valid token to play.
        </div>
      </div>

      {/* Channels table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Category</th>
              <th>Type</th>
              <th>Stream URL</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                  No video channels yet. Click "Add Channel" to create one.
                </td>
              </tr>
            ) : (
              channels.map((ch) => (
                <tr key={ch._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {ch.thumbnail ? (
                        <img
                          src={ch.thumbnail}
                          alt={ch.name}
                          style={{
                            width: 48, height: 36, borderRadius: 6,
                            objectFit: 'cover', border: '1px solid #E2E8F0',
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 48, height: 36, borderRadius: 6,
                          background: 'linear-gradient(135deg, #06B6D4, #1E3A8A)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <HiPlay size={18} color="#fff" />
                        </div>
                      )}
                      <div>
                        <strong style={{ fontSize: 14 }}>{ch.name}</strong>
                        <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                          {ch.requiresPayment && (
                            <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626' }}>PREMIUM</span>
                          )}
                          {ch.hidden && (
                            <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#F3E8FF', color: '#9333EA' }}>HIDDEN</span>
                          )}
                          {ch.visibleOnWebsite === false && (
                            <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: '#F1F5F9', color: '#64748B' }}>OFF-WEB</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{ch.category}</span>
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4,
                        background: ch.linkType === 'DRM' ? '#FEF3C7' : ch.linkType === 'DASH' ? '#DBEAFE' : '#D1FAE5',
                        color: ch.linkType === 'DRM' ? '#D97706' : ch.linkType === 'DASH' ? '#2563EB' : '#059669',
                      }}
                    >
                      {ch.linkType || 'HLS'}
                    </span>
                  </td>
                  <td>
                    <code style={{
                      fontSize: 11, background: '#F1F5F9', padding: '3px 6px',
                      borderRadius: 4, wordBreak: 'break-all', maxWidth: 200,
                      display: 'inline-block',
                    }}>
                      {ch.streamUrl.length > 50 ? ch.streamUrl.slice(0, 50) + '...' : ch.streamUrl}
                    </code>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleActive(ch)}
                      className={`status-toggle ${ch.isActive ? 'active' : 'inactive'}`}
                      title={ch.isActive ? 'Click to hide' : 'Click to show'}
                    >
                      {ch.isActive ? <><HiEye size={14} /> Active</> : <><HiEyeOff size={14} /> Hidden</>}
                    </button>
                  </td>
                  <td style={{ textAlign: 'center' }}>{ch.sortOrder}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-icon edit" onClick={() => openEdit(ch)} title="Edit">
                        <HiPencil size={16} />
                      </button>
                      <button className="btn-icon delete" onClick={() => handleDelete(ch._id)} title="Delete">
                        <HiTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Channel' : 'Add Video Channel'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <HiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {/* Name */}
              <div className="form-group">
                <label>Channel Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Azam Sport 1"
                  required
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of the channel"
                  rows={2}
                />
              </div>

              {/* Stream URL */}
              <div className="form-group">
                <label>Stream URL *</label>
                <input
                  type="url"
                  value={form.streamUrl}
                  onChange={(e) => setForm({ ...form, streamUrl: e.target.value })}
                  placeholder="http://example.com/live/stream.m3u8"
                  required
                />
                <small style={{ color: '#64748B', fontSize: 12 }}>
                  Supports .m3u8 (HLS) and .mpd (DASH/DRM) stream URLs
                </small>
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Category</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CATEGORY_PRESETS.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`chip-btn ${form.category === cat ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, category: cat })}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Or type custom category"
                  style={{ marginTop: 8 }}
                />
              </div>

              {/* Thumbnail / Icon */}
              <div className="form-group">
                <label>Thumbnail Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {form.thumbnail ? (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={form.thumbnail}
                        alt="Thumbnail"
                        style={{ width: 100, height: 60, borderRadius: 8, objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, thumbnail: '' })}
                        style={{
                          position: 'absolute', top: -6, right: -6,
                          width: 20, height: 20, borderRadius: '50%',
                          background: '#EF4444', color: '#fff', border: 'none',
                          cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        width: 100, height: 60, borderRadius: 8,
                        border: '2px dashed #CBD5E1', display: 'flex',
                        flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer',
                        color: '#64748B', fontSize: 11, gap: 2,
                      }}
                    >
                      {uploading ? (
                        <span>Uploading...</span>
                      ) : (
                        <>
                          <HiUpload size={16} />
                          <span>Upload</span>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    style={{ display: 'none' }}
                  />
                  <input
                    type="url"
                    value={form.thumbnail}
                    onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                    placeholder="…or paste icon/thumbnail URL"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Link Type */}
              <div className="form-group">
                <label>Link Type</label>
                <select
                  value={form.linkType}
                  onChange={(e) => setForm({ ...form, linkType: e.target.value })}
                >
                  {LINK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <small style={{ color: '#64748B', fontSize: 12 }}>
                  HLS (.m3u8) or DASH/DRM (.mpd). DRM streams need key id + key below.
                </small>
              </div>

              {/* DRM fields */}
              {(form.linkType === 'DRM' || form.linkType === 'DASH') && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>DRM Key ID (kid) {form.linkType === 'DRM' ? '*' : ''}</label>
                    <input
                      type="text"
                      value={form.drmKid}
                      onChange={(e) => setForm({ ...form, drmKid: e.target.value })}
                      placeholder="32-char hex key id"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>DRM Key</label>
                    <input
                      type="text"
                      value={form.drmKey}
                      onChange={(e) => setForm({ ...form, drmKey: e.target.value })}
                      placeholder="32-char hex key"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                </div>
              )}

              {/* Flags */}
              <div className="form-group">
                <label>Options</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={form.requiresPayment}
                      onChange={(e) => setForm({ ...form, requiresPayment: e.target.checked })}
                    /> Requires Payment (Premium)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={form.visibleOnWebsite}
                      onChange={(e) => setForm({ ...form, visibleOnWebsite: e.target.checked })}
                    /> Visible on Website
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={form.hidden}
                      onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
                    /> Hidden
                  </label>
                </div>
              </div>

              {/* Sort Order + Active */}
              <div style={{ display: 'flex', gap: 16 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    min="0"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Status</label>
                  <select
                    value={form.isActive ? 'true' : 'false'}
                    onChange={(e) => setForm({ ...form, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Hidden</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Channel' : 'Create Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const statBoxStyle = {
  background: '#F8FAFC',
  border: '1px solid #E2E8F0',
  borderRadius: 10,
  padding: 12,
};
